/*
  vms-model-badge.js  (VMS, 2026-09-15)

  Touch flag for the persistent 3D badge and hint on the product-model slide. On
  a pointerdown or touchstart anywhere in a <product-model>, add the class
  "vms-model--touched" to that element. The CSS rule
  product-model.vms-model--touched .vms-model-hint { display: none } then hides the
  "Tap, then drag to rotate" hint after the first interaction while the "3D view"
  badge stays. The badge/hint spans are painted by assets/component-product-model.css.

  Listeners are registered with capture: true so model-viewer (a descendant) cannot
  swallow the event before we see it, and passive: true so we never block scrolling
  or rotation. Each product-model is initialized once (guarded by a data attribute).

  [VMS] 2026-09-15 (owner, Fix 4): the hint must return when the fullscreen modal is
  re-opened. The earlier build removed the listeners after the first hit, so once the
  flag was cleared nothing re-hid the hint. The handler now STAYS attached (it only
  toggles a class, so re-firing is idempotent), and when the media modal closes we
  clear "vms-model--touched" from the product-models INSIDE that modal only. The next
  pointerdown inside the model then re-hides the hint. The gallery's own inline
  product-model (desktop) is never reset on modal close.

  Loaded with defer from sections/vms-main-product.liquid inside the first_3d_model guard.
*/
(function () {
  'use strict';

  function markTouched(model) {
    model.classList.add('vms-model--touched');
  }

  function initModel(model) {
    if (model.dataset.vmsBadgeInit === '1') {
      return;
    }
    model.dataset.vmsBadgeInit = '1';

    // Handler stays attached (idempotent add) so it works again after a modal-close
    // reset. Capture so a descendant model-viewer cannot swallow the event first;
    // passive so scroll and rotation are never blocked.
    var handler = function () {
      markTouched(model);
    };

    model.addEventListener('pointerdown', handler, { passive: true, capture: true });
    model.addEventListener('touchstart', handler, { passive: true, capture: true });
  }

  function initAll() {
    var models = document.querySelectorAll('product-model');
    for (var i = 0; i < models.length; i++) {
      initModel(models[i]);
    }
  }

  // Fix 4: clear the touch flag on the product-models inside a closed modal so the
  // hint shows again on the next open. Scoped to the modal, so the gallery's inline
  // product-model keeps its state.
  function resetModal(modal) {
    var models = modal.querySelectorAll('product-model');
    for (var i = 0; i < models.length; i++) {
      models[i].classList.remove('vms-model--touched');
    }
  }

  function initModalReset(modal) {
    if (modal.dataset.vmsModalResetInit === '1') {
      return;
    }
    modal.dataset.vmsModalResetInit = '1';

    // The media modal (Dawn ModalDialog) toggles the `open` attribute on show/hide.
    // Reset whenever `open` is removed. This covers the close button, Escape and the
    // click-outside dismiss, all of which run hide().
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName === 'open' && !modal.hasAttribute('open')) {
          resetModal(modal);
        }
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });

    // Belt and braces: reset directly on the close button and Escape too, in case the
    // open-attribute contract ever changes.
    var closeBtn = modal.querySelector('[id^="ModalClose-"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        resetModal(modal);
      });
    }
    modal.addEventListener('keyup', function (event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') {
        resetModal(modal);
      }
    });
  }

  function initAllModals() {
    // Dawn moves the modal to document.body; class is "product-media-modal media-modal".
    var modals = document.querySelectorAll('product-modal.media-modal');
    for (var i = 0; i < modals.length; i++) {
      initModalReset(modals[i]);
    }
  }

  function init() {
    initAll();
    initAllModals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // [VMS] 2026-09-15 (Sonnet review): the section script tag lives inside
  // sections/vms-main-product.liquid, so a theme-editor setting change re-renders the
  // section (shopify:section:load) and replaces the product-model and product-modal nodes
  // without re-running this IIFE. Re-run init on that event so the fresh nodes get their
  // listeners and the modal reset observer. The per-element dataset guards
  // (vmsBadgeInit / vmsModalResetInit) skip any node that survived the re-render, so no
  // duplicate listeners or observers are attached. Dawn also moves the modal to
  // document.body; initAllModals queries the whole document, so a moved modal is found.
  document.addEventListener('shopify:section:load', init);
})();
